import { useLazyQuery } from '@apollo/client';
import {
  DNIType,
  GetUserDocumentT,
  IdentityValidation,
  IdentityValidationQueryActions,
  UserDocument,
  UserDocumentResult,
} from '@contexts/IdentityValidation/domain';
import { AptunoApolloLink } from '@shared/data-access';
import { ResponseApolloErrorMapping } from '@shared/data-mappers';
import {
  BaseQueryResponse2,
  ResourceNotFoundError,
  Response,
} from '@shared/models';
import { DocumentTypes } from '@shared/types';

import { GetIdentityValidationResponseDto } from '../dtos';
import { IdentityValidationResponseDtoMapper } from '../mappers';
import { getIdentityValidation, gqlGetUserDocument } from '../schemas/queries';
import { useCallback, useMemo } from 'react';

const mapDocument = (result: UserDocumentResult) => {
  const userDocument: UserDocument = {
    identityDocumentCode: result.identityDocumentCode,
    identityDocumentType:
      result.identityDocumentType === DocumentTypes.PA
        ? DNIType.PASSPORT
        : DNIType[result.identityDocumentType],
  };
  return userDocument;
};

export const GqlIdentityValidationQueryActions: IdentityValidationQueryActions =
  {
    useGetIdentityValidation: function (): BaseQueryResponse2<
      void,
      IdentityValidation
    > {
      const [action, { data, loading, error, refetch }] =
        useLazyQuery<GetIdentityValidationResponseDto>(getIdentityValidation, {
          errorPolicy: 'all',
          context: { clientName: AptunoApolloLink.CREDIT_STUDY },
        });

      const result = useMemo((): Response<IdentityValidation> => {
        if (!!error)
          return new Response.Failure(ResponseApolloErrorMapping(error));

        if (!data) return Response.notPerformed;

        const foundIdentityValidation = data.getIdentityValidation?.value;

        if (!foundIdentityValidation)
          return new Response.Failure(
            new ResourceNotFoundError('identityValidation')
          );

        return new Response.Success(
          IdentityValidationResponseDtoMapper.toIdentityValidationModel(
            foundIdentityValidation
          )
        );
      }, [data, error]);

      const recall = useCallback(
        async (): Promise<void> => refetch().then(() => void 0),
        [refetch]
      );

      return {
        loading,
        recall,
        result,
        get: () => action(),
      };
    },

    useGetUserDocument: function (): GetUserDocumentT {
      const [action, { data, loading, error, refetch }] = useLazyQuery<{
        getAuthenticatedUser: UserDocumentResult;
      }>(gqlGetUserDocument, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CORE },
        fetchPolicy: 'no-cache',
      });

      const recall = useCallback(
        async (): Promise<UserDocument | undefined> =>
          refetch().then(
            (data) => data && mapDocument(data.data.getAuthenticatedUser)
          ),
        [refetch]
      );

      const userDocument = useMemo(
        () => data && mapDocument(data.getAuthenticatedUser),
        [data]
      );

      const errorResult = useMemo(
        () => error && ResponseApolloErrorMapping(error),
        [error]
      );

      return {
        loading,
        recall,
        error: errorResult,
        userDocument,
        getUserDocument: () => action(),
      };
    },
  };
